import { KEYS_TAGS } from '@components/imageHandling/common';
import { Perspective } from '@v2/pages/common/enums';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { ToolsDetailsScreenName } from '@v2/pages/tools/details/navigation/config';
import { ToolsDetailsScreen } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';

export const TEXT_MAPPING: Record<string, string> = {
  Part: 'Part',
  ProductionBatch: 'Batch',
  NcFile: 'NC File',
  Tool: 'Tool',
  ImageDocument: 'Image',
  OtherDocument: 'Document',
};

const FALLBACK_IDS = ['PartId', 'NcFileId', 'ToolId', 'DocumentId', 'ProductionOperationId', 'ProductionOrderId'] as const;

const FALLBACK_TYPES: Record<string, string> = {
  PartId: 'Part',
  NcFileId: 'NcFile',
  ToolId: 'Tool',
  DocumentId: 'ImageDocument',
  ProductionOperationId: 'ProductionOperation',
  ProductionOrderId: 'ProductionOrder',
};

interface LookupKeyField {
  name: string;
  value: any;
}

export interface ParsedSuggestionItem {
  id: any;
  type: string | undefined;
  itemName: string;
}

export const parseSuggestionItem = (lookupKeyFields: LookupKeyField[], text: string): ParsedSuggestionItem => {
  let id = lookupKeyFields.find((i) => i.name === 'ItemId')?.value;
  let type = lookupKeyFields.find((i) => i.name === 'ItemType')?.value;
  const itemName =
    lookupKeyFields.find((i) => i.name === 'ItemName')?.value ||
    lookupKeyFields.find((i) => i.name === 'Name')?.value ||
    text.replace(/<p>/g, '');

  if (!type) {
    lookupKeyFields.find((i) => {
      const el = (FALLBACK_IDS as readonly string[]).includes(i.name);
      if (el) {
        type = FALLBACK_TYPES[i.name];
        id = i.value;
        return true;
      }
    });
  }

  return { id, type, itemName };
};

export const buildPopupModeItem = (type: string, id: any, itemName: string): Record<string, any> => {
  const item: Record<string, any> = {};
  switch (type) {
    case 'Part':
      item.partId = id;
      item.partName = itemName;
      break;
    case 'NcFile':
      item.ncFileId = id;
      item.ncFileName = itemName;
      break;
    case 'Tool':
      item.toolId = id;
      item.toolName = itemName;
      break;
    case 'ProductionOperation':
    case 'Operation':
      item.productionOperationId = id;
      item.productionOperationNumber = itemName;
      break;
    case 'ProductionOrder':
      item.productionOrderId = id;
      item.productionOrderNumber = itemName;
      break;
    default:
      item.id = id;
      item.name = itemName;
  }
  return item;
};

export const getNavigationParams = (type: string, id: any, itemName: string) => {
  switch (type) {
    case 'Part':
      return {
        mainScreen: PartDetailsScreenName,
        subScreen: PartDetailsScreen.PART,
        params: { perspective: Perspective.PART, partId: id },
      };
    case 'NcFile':
      return {
        mainScreen: NCFileDetailsScreenName,
        subScreen: NCFileDetailsScreen.NC_FILE,
        params: { perspective: Perspective.NC_FILE, ncFileId: id },
      };
    case 'Tool':
      return {
        mainScreen: ToolsDetailsScreenName,
        subScreen: ToolsDetailsScreen.TOOLS,
        params: { perspective: Perspective.TOOLS, toolId: id },
      };
    case 'Operation':
    case 'ProductionOperation':
      return {
        mainScreen: JobDetailsScreenName,
        subScreen: JobDetailsScreen.JOBS,
        params: { perspective: Perspective.JOBS, productionOperationId: id },
      };
    case 'ProductionOrder':
      return {
        mainScreen: ProductionOrderDetailsScreenName,
        subScreen: ProductionOrderDetailsScreen.ORDER,
        params: { perspective: Perspective.ORDER, productionOrderId: id },
      };
    case 'ProductionBatch':
      return {
        mainScreen: BatchDetailsScreenName,
        subScreen: BatchDetailsScreen.BATCH,
        params: { perspective: Perspective.BATCH, batchId: id, batchNumber: itemName },
      };
    default:
      return null;
  }
};

const SCOPE_ID_MAP: Record<string, number> = {
  Part: KEYS_TAGS.Part,
  NcFile: KEYS_TAGS.Clamping,
  ProductionBatch: KEYS_TAGS.ProductionBatch,
  ProductionOrder: KEYS_TAGS.ProductionOrder,
  Operation: KEYS_TAGS.Operation,
};

export const getAttachScopeId = (itemType: string): number | null => {
  return SCOPE_ID_MAP[itemType] ?? null;
};
